import { useState, useEffect } from 'react';
import { useLoaderData, useNavigate, Form, useActionData } from 'react-router';
import type { LoaderFunctionArgs, ActionFunctionArgs } from 'react-router';
import { 
  Calendar, Building2, MapPin, Clock, ArrowLeft, Edit, Trash2, 
  FileText, ExternalLink, Save, X, TrendingUp, AlertCircle, 
  CheckCircle, Target, Star
} from 'lucide-react';
import { requireUser } from '../../../lib/session.server';
import { applicationsApi } from '../../../lib/api';
import { Card, Button, Badge } from '../../../components/ui';
import { ApplicationStatusChip, useToastActions } from '../../../components/ui';
import type { Application, User } from '../../../types';
import type { ApplicationStatus } from '../../../types/applications';
import { APPLICATION_STATUS_CONFIG } from '../../../types/applications';

interface ApplicationDetailData {
  user: User;
  application: Application;
}

export async function loader({ request, params }: LoaderFunctionArgs) {
  const user = await requireUser(request);
  const applicationId = params.id as string;

  try {
    const application = await applicationsApi.getApplication(applicationId);
    
    return {
      user,
      application,
    };
  } catch (error) {
    throw new Response('Application not found', { status: 404 });
  }
}

export async function action({ request, params }: ActionFunctionArgs) {
  await requireUser(request);
  const applicationId = params.id as string;
  const formData = await request.formData();
  const intent = formData.get('intent') as string;

  try {
    switch (intent) {
      case 'delete': {
        await applicationsApi.deleteApplication(applicationId);
        return { success: true, message: 'Application deleted', redirect: '/applications' };
      }
      
      case 'update-status': {
        const status = String(formData.get('status')) as ApplicationStatus;
        await applicationsApi.updateApplication(applicationId, { status });
        return { success: true, message: 'Status updated successfully' };
      }
      
      case 'update-notes': {
        const notes = formData.get('notes') as string;
        await applicationsApi.updateApplication(applicationId, { notes });
        return { success: true, message: 'Notes saved successfully' };
      }
      
      case 'schedule-follow-up': {
        const followUpDate = formData.get('followUpDate') as string;
        const note = formData.get('note') as string;
        await applicationsApi.scheduleFollowUp(applicationId, followUpDate, note || undefined);
        return { success: true, message: 'Follow-up scheduled' };
      }
      
      case 'rate-company': {
        const rating = parseInt(formData.get('rating') as string);
        await applicationsApi.updateApplication(applicationId, { company_rating: rating });
        return { success: true, message: 'Rating saved' };
      }
      
      default:
        return { success: false, error: 'Invalid action' };
    }
  } catch (error) {
    return { success: false, error: 'Action failed' };
  }
}

export function ApplicationDetailPage() {
  const { application } = useLoaderData() as ApplicationDetailData;
  const actionData = useActionData() as { success?: boolean; error?: string; message?: string };
  const navigate = useNavigate();
  const { success, error } = useToastActions();
  
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notes, setNotes] = useState(application.notes || '');
  const [showFollowUp, setShowFollowUp] = useState(false);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getDaysAgo = (dateString: string): number => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const needsFollowUp = getDaysAgo(application.applied_at) > 14 && application.status === 'applied';

  // Handle action data
  useEffect(() => {
    if (actionData?.success) {
      success('Action Successful', actionData.message || 'Operation completed.');
      if (actionData.message?.includes('deleted')) {
        navigate('/applications');
      }
      if (actionData.message?.includes('Notes')) {
        setIsEditingNotes(false);
      }
    } else if (actionData?.error) {
      error('Action Failed', actionData.error);
    }
  }, [actionData, success, error, navigate]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <Button variant="ghost" size="sm" onClick={() => navigate('/applications')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <FileText className="h-6 w-6 text-primary" />
          <h1 className="text-3xl font-bold text-foreground">Application Details</h1>
        </div>
        <p className="text-muted-foreground ml-11">
          Manage and track your job application progress.
        </p>
      </div>

      {/* Quick Actions */}
      <Card>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {needsFollowUp && (
              <Badge variant="secondary" className="gap-1 text-amber-600 bg-amber-500/10">
                <TrendingUp className="h-3 w-3" />
                Follow-up needed
              </Badge>
            )}
            <span className="text-sm text-muted-foreground">
              {getDaysAgo(application.applied_at)} days since application
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            {application.job_url && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => window.open(application.job_url, '_blank')}
                className="gap-1"
              >
                <ExternalLink className="h-4 w-4" />
                View Job
              </Button>
            )}
            
            <Form method="post" className="inline">
              <input type="hidden" name="intent" value="delete" />
              <Button
                type="submit"
                variant="secondary"
                size="sm"
                onClick={(e) => {
                  if (!confirm('Are you sure you want to delete this application?')) {
                    e.preventDefault();
                  }
                }}
                className="gap-1 text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </Form>
          </div>
        </div>
      </Card>

      {/* Job Information */}
      <Card>
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-3 bg-primary/10 rounded-xl">
                <Building2 className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-foreground">{application.job_title}</h2>
                <div className="flex items-center gap-4 mt-2 text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Building2 className="h-4 w-4" />
                    {application.company}
                  </span>
                  <span className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {application.location}
                  </span>
                  {application.salary_range && (
                    <span className="font-medium text-foreground">{application.salary_range}</span>
                  )}
                </div>
                <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    Applied {formatDate(application.applied_at)}
                  </span>
                  {application.updated_at !== application.applied_at && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      Updated {formatDate(application.updated_at)}
                    </span>
                  )}
                  <span className="text-xs px-2 py-1 bg-muted/30 rounded-lg">
                    Source: {application.source || 'manual'}
                  </span>
                </div>
              </div>
            </div>
            
            <ApplicationStatusChip status={application.status} />
          </div>
        </div>
      </Card>

      {/* Status Update */}
      <Card>
        <div className="space-y-4">
          <h3 className="font-semibold text-foreground">Update Status</h3>
          <Form method="post" className="flex flex-wrap gap-2">
            <input type="hidden" name="intent" value="update-status" />
            {(['applied', 'interviewing', 'offered', 'rejected', 'withdrawn'] as const).map((status) => (
              <Button
                key={status}
                type="submit"
                name="status"
                value={status}
                variant={application.status === status ? 'default' : 'secondary'}
                size="sm"
                className={`gap-1 ${
                  status === 'offered' ? 'text-green-600 border-green-600 hover:bg-green-50' :
                  status === 'rejected' ? 'text-red-600 border-red-600 hover:bg-red-50' :
                  status === 'interviewing' ? 'text-amber-600 border-amber-600 hover:bg-amber-50' :
                  ''
                }`}
              >
                {status === 'applied' && <Clock className="h-4 w-4" />}
                {status === 'interviewing' && <AlertCircle className="h-4 w-4" />}
                {status === 'offered' && <CheckCircle className="h-4 w-4" />}
                {status === 'rejected' && <X className="h-4 w-4" />}
                {status === 'withdrawn' && <Target className="h-4 w-4" />}
                {APPLICATION_STATUS_CONFIG[status as ApplicationStatus]?.label || status}
              </Button>
            ))}
          </Form>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Notes */}
        <Card className="lg:col-span-1">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-foreground">Notes</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditingNotes(!isEditingNotes)}
                className="gap-1"
              >
                {isEditingNotes ? <X className="h-4 w-4" /> : <Edit className="h-4 w-4" />}
                {isEditingNotes ? 'Cancel' : 'Edit'}
              </Button>
            </div>
            
            {isEditingNotes ? (
              <Form method="post" className="space-y-4">
                <input type="hidden" name="intent" value="update-notes" />
                <textarea
                  name="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={6}
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                  placeholder="Add notes about this application, interview details, follow-ups, etc..."
                />
                <div className="flex gap-2">
                  <Button type="submit" size="sm" className="gap-1">
                    <Save className="h-4 w-4" />
                    Save Notes
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setIsEditingNotes(false);
                      setNotes(application.notes || '');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            ) : (
              <div className="min-h-[120px]">
                <p className="text-muted-foreground whitespace-pre-wrap">
                  {application.notes || 'No notes added yet. Click Edit to add notes about this application.'}
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Company Rating & Actions */}
        <Card className="lg:col-span-1">
          <div className="space-y-4">
            <h3 className="font-semibold text-foreground">Quick Actions</h3>
            
            {/* Company Rating */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Rate Company Experience
              </label>
              <Form method="post" className="flex items-center gap-2">
                <input type="hidden" name="intent" value="rate-company" />
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      type="submit"
                      name="rating"
                      value={rating}
                      className={`p-1 rounded transition-colors ${
                        (application.company_rating || 0) >= rating
                          ? 'text-amber-500'
                          : 'text-muted-foreground/50 hover:text-amber-500'
                      }`}
                    >
                      <Star className="h-5 w-5 fill-current" />
                    </button>
                  ))}
                </div>
                {application.company_rating && (
                  <span className="text-sm text-muted-foreground">
                    ({application.company_rating}/5)
                  </span>
                )}
              </Form>
            </div>

            {/* Follow-up Actions */}
            {needsFollowUp && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <TrendingUp className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-foreground mb-1">Follow-up Recommended</h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      It's been {getDaysAgo(application.applied_at)} days since you applied. 
                      Consider following up with the hiring team.
                    </p>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setShowFollowUp(true)}
                      className="gap-1"
                    >
                      <Calendar className="h-4 w-4" />
                      Schedule Follow-up
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Cover Letter */}
      {application.cover_letter && (
        <Card>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-foreground">Cover Letter</h3>
            </div>
            <div className="bg-muted/30 rounded-xl p-4">
              <p className="whitespace-pre-wrap text-foreground leading-relaxed">
                {application.cover_letter}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Timeline */}
      {application.timeline && application.timeline.length > 0 && (
        <Card>
          <div className="space-y-6">
            <h3 className="font-semibold text-foreground">Application Timeline</h3>
            <div className="space-y-4">
              {application.timeline.map((event, index) => (
                <div key={event.id || index} className="flex gap-4">
                  <div className="flex-shrink-0 w-3 h-3 mt-1 rounded-full bg-primary"></div>
                  <div className="flex-1 pb-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-foreground">{event.title}</span>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(event.event_date)}
                      </span>
                    </div>
                    {event.description && (
                      <p className="text-muted-foreground text-sm">{event.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Follow-up Modal */}
      {showFollowUp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <Card className="max-w-md w-full mx-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground">Schedule Follow-up</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowFollowUp(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <Form method="post" className="space-y-4">
                <input type="hidden" name="intent" value="schedule-follow-up" />
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Follow-up Date
                  </label>
                  <input
                    type="date"
                    name="followUpDate"
                    required
                    className="w-full px-3 py-2 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Note (optional)
                  </label>
                  <textarea
                    name="note"
                    rows={3}
                    placeholder="Remind me to..."
                    className="w-full px-3 py-2 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button type="submit" className="flex-1">
                    Schedule
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setShowFollowUp(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}